import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  Checkbox,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { API_BASE_URL } from "../config";
import { exportAuditLog } from "../exportExcel";

const AUDIT_API = `${API_BASE_URL}/audit-logs`;
const EVENTS_API = `${API_BASE_URL}/events`;

// The target types emitted by the backend routers. Kept as a fixed list so the
// filter offers meaningful options without a distinct-values round trip.
const TARGET_TYPES = [
  "incident",
  "incident_note",
  "personnel",
  "event",
  "event_attendance",
  "event_team",
  "event_race",
  "event_participant",
  "event_location",
  "inventory_item",
  "inventory_type",
  "inventory_kit",
  "inventory_allocation",
  "guideline",
  "link",
  "document",
  "qualification_certificate",
  "reminder",
  "phone_find",
  "event_route",
  "safety_assessment",
  "safety_route_settings",
];

// The actions emitted by core/audit.py's callers, likewise a fixed list.
const ACTIONS = ["create", "update", "delete", "read", "import", "share"];

// Sentinel value for the tick-all/untick-all row at the top of a multi-select's
// menu. Never stored in the filter, so seeing it in an onChange means it was the
// row that was clicked.
const TOGGLE_ALL = " all";

const ACTION_COLOR = {
  create: "success",
  update: "info",
  delete: "error",
  read: "default",
  export: "warning",
  import: "secondary",
  share: "secondary",
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

// An SMS send's outcome, as recorded by core/incident_shares.audit_meta.
const smsSummary = (sms) =>
  sms && typeof sms === "object" && typeof sms.sent === "number"
    ? `Texted ${sms.target}: ${sms.sent} of ${sms.recipients} sent`
    : "";

// Short human summary of a row's metadata.
function metaSummary(meta) {
  if (!meta || typeof meta !== "object") return "";
  if (meta.reason === "gdpr_erasure") {
    const n = meta.notes_deleted;
    return `GDPR erasure — personal & clinical details removed${
      typeof n === "number" ? ` (${n} note${n === 1 ? "" : "s"} deleted)` : ""}`;
  }
  if (smsSummary(meta)) return smsSummary(meta);  // a share
  if (Array.isArray(meta.changed) && meta.changed.length) {
    // A reallocation may have texted the new allocatee — same row, so say both.
    const sms = smsSummary(meta.allocation_sms);
    return `Changed: ${meta.changed.join(", ")}${sms ? ` · ${sms}` : ""}`;
  }
  if (typeof meta.created === "number" || typeof meta.skipped === "number")
    return `Imported ${meta.created ?? 0}, skipped ${meta.skipped ?? 0}`;
  if (meta.bulk && typeof meta.deleted === "number") return `Bulk delete: ${meta.deleted}`;
  if (Array.isArray(meta.permission_roles)) return `Roles: ${meta.permission_roles.join(", ") || "(none)"}`;
  return "";
}

const BLANK_FILTERS = { actor: "", event_id: "", target_type: [], action: [], from: "", to: "" };

export default function Audit() {
  const [filters, setFilters] = useState(BLANK_FILTERS);
  const [rows, setRows] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${EVENTS_API}/`).then(res => setEvents(res.data || [])).catch(() => setEvents([]));
  }, []);

  const load = useCallback(() => {
    // URLSearchParams rather than a plain object because Table and Action are
    // multi-select: the backend wants the repeated form (?action=a&action=b),
    // which axios' default array serialisation ("action[]=a") wouldn't produce.
    const params = new URLSearchParams();
    params.set("limit", "1000");
    if (filters.actor.trim()) params.set("actor", filters.actor.trim());
    if (filters.event_id !== "") params.set("event_id", filters.event_id);
    filters.target_type.forEach(t => params.append("target_type", t));
    filters.action.forEach(a => params.append("action", a));
    if (filters.from) params.set("from", new Date(filters.from).toISOString());
    if (filters.to) params.set("to", new Date(filters.to).toISOString());
    setLoading(true);
    axios.get(`${AUDIT_API}/`, { params })
      .then(res => setRows(res.data || []))
      .catch(() => setRows([])) // the global interceptor surfaces the error
      .finally(() => setLoading(false));
  }, [filters]);

  // Load once on mount (most recent 1000, unfiltered).
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key) => (e) => setFilters(f => ({ ...f, [key]: e.target.value }));
  const clear = () => { setFilters(BLANK_FILTERS); };

  // A multi-select chip filter over a fixed list of strings; none selected = no
  // filter, which is the same set of rows as all selected.
  //
  // The menu opens with a tick-all/untick-all row, because the useful queries are
  // subtractive: "everything except reads", "everything except the incident tables,
  // which are noisy at an event". Ticking all then unticking one or two is the
  // shortest way to say that, so the summary is written the same way round —
  // "Except: read" rather than eighteen chips.
  const multiSelect = (key, label, options, allLabel) => {
    const selected = filters[key];
    const allOn = selected.length === options.length;
    return (
      <TextField
        label={label} select size="small" sx={{ minWidth: 220, maxWidth: 340 }}
        value={selected}
        onChange={e => {
          const v = typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value;
          setFilters(f => (v.includes(TOGGLE_ALL)
            ? { ...f, [key]: allOn ? [] : [...options] }
            : { ...f, [key]: v }));
        }}
        slotProps={{
          inputLabel: { shrink: true },
          select: {
            multiple: true,
            displayEmpty: true,
            renderValue: (chosen) => {
              if (chosen.length === 0 || chosen.length === options.length)
                return <Typography variant="body2" color="text.secondary">{allLabel}</Typography>;
              // Past halfway it's shorter — and closer to what was meant — to name
              // what was left out.
              const invert = chosen.length > options.length / 2;
              const shown = invert ? options.filter(o => !chosen.includes(o)) : chosen;
              return (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                  {invert && <Typography variant="body2" color="text.secondary">Except</Typography>}
                  {shown.map(v => <Chip key={v} label={v} size="small" />)}
                </Box>
              );
            },
          },
        }}
      >
        <MenuItem value={TOGGLE_ALL} sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
          <Checkbox checked={allOn} indeterminate={selected.length > 0 && !allOn} />
          <ListItemText primary={allOn ? "Untick all" : "Tick all"} />
        </MenuItem>
        {options.map(o => (
          <MenuItem key={o} value={o}>
            <Checkbox checked={selected.indexOf(o) > -1} />
            <ListItemText primary={o} />
          </MenuItem>
        ))}
      </TextField>
    );
  };

  const eventLabel = (id) => {
    const e = events.find(ev => ev.id === id);
    return e ? (e.label_short || e.label_long || `Event ${id}`) : (id != null ? `Event ${id}` : "");
  };

  return (
    <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider" }}>
      <Typography variant="h5" gutterBottom>Audit log</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Most recent 1000 entries matching the filters.
      </Typography>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end", mb: 2 }}>
        <TextField label="Actor" placeholder="name or email" value={filters.actor}
                   onChange={set("actor")} sx={{ minWidth: 200 }} size="small"
                   onKeyDown={(e) => { if (e.key === "Enter") load(); }} />
        <TextField label="Event" select value={filters.event_id} onChange={set("event_id")}
                   sx={{ minWidth: 220 }} size="small">
          <MenuItem value="">All events</MenuItem>
          {events.map(e => (
            <MenuItem key={e.id} value={e.id}>{e.label_long || e.label_short || `Event ${e.id}`}</MenuItem>
          ))}
        </TextField>
        {multiSelect("target_type", "Table", TARGET_TYPES, "All tables")}
        {multiSelect("action", "Action", ACTIONS, "All actions")}
        <TextField label="From" type="datetime-local" value={filters.from} onChange={set("from")}
                   size="small" slotProps={{ inputLabel: { shrink: true } }} />
        <TextField label="To" type="datetime-local" value={filters.to} onChange={set("to")}
                   size="small" slotProps={{ inputLabel: { shrink: true } }} />
        <Button variant="contained" startIcon={<SearchIcon />} onClick={load}>Search</Button>
        <Button startIcon={<ClearIcon />} onClick={clear}>Clear</Button>
        <Button variant="outlined" startIcon={<FileDownloadIcon />}
                disabled={rows.length === 0}
                onClick={() => exportAuditLog(rows, { eventLabel, metaSummary })}>
          Export to Excel
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {rows.length} {rows.length === 1 ? "entry" : "entries"}
            {rows.length === 1000 ? " (showing the most recent 1000 — narrow the filters to see more)" : ""}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Actor</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Table</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Detail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow><TableCell colSpan={8}>
                    <Typography color="text.secondary" sx={{ py: 2 }}>No matching entries.</Typography>
                  </TableCell></TableRow>
                ) : rows.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{fmt(r.at)}</TableCell>
                    <TableCell>{r.actor_name || r.actor_email || "—"}</TableCell>
                    <TableCell>
                      <Chip size="small" label={r.action} color={ACTION_COLOR[r.action] || "default"} />
                    </TableCell>
                    <TableCell>{r.target_type}</TableCell>
                    <TableCell>{r.target_name || "—"}</TableCell>
                    <TableCell>{r.target_id ?? "—"}</TableCell>
                    <TableCell>{r.event_id != null ? eventLabel(r.event_id) : "—"}</TableCell>
                    <TableCell>{metaSummary(r.meta)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Paper>
  );
}
